import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createListing } from '../lib/supabase';

// Parse CSV — handles multiline quoted fields and spaces in headers
function parseCSV(text) {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  function tokenize(str) {
    const rows = [];
    let row = [], cur = '', inQ = false, i = 0;
    while (i < str.length) {
      const ch = str[i];
      if (ch === '"') {
        if (inQ && str[i+1] === '"') { cur += '"'; i += 2; continue; }
        inQ = !inQ; i++; continue;
      }
      if (ch === ',' && !inQ) { row.push(cur.trim()); cur = ''; i++; continue; }
      if (ch === '\n' && !inQ) { row.push(cur.trim()); rows.push(row); row = []; cur = ''; i++; continue; }
      if (ch === '\n' && inQ) { cur += ' '; i++; continue; }
      cur += ch; i++;
    }
    if (cur.trim() || row.length) { row.push(cur.trim()); rows.push(row); }
    return rows.filter(r => r.some(c => c));
  }

  const rows = tokenize(normalized);
  if (rows.length < 2) throw new Error('File must have a header row and at least one data row.');

  const headers = rows[0].map(h => h.replace(/\s+/g, '').toLowerCase().replace(/[^a-z_]/g, ''));

  return rows.slice(1).map((cols, i) => {
    const row = { _row: i + 2 };
    headers.forEach((h, idx) => { row[h] = (cols[idx] || '').trim(); });
    return row;
  });
}

// Flexible column name matching
function getField(row, ...keys) {
  for (const key of keys) {
    for (const col of Object.keys(row)) {
      if (col.replace(/[^a-z]/g, '').includes(key.replace(/[^a-z]/g, ''))) {
        return row[col];
      }
    }
  }
  return '';
}

function validateRows(rows) {
  const valid = [], errors = [];
  rows.forEach(row => {
    const title = getField(row, 'title', 'name', 'item', 'product');
    const priceRaw = getField(row, 'price', 'cost', 'amount', 'usd');
    const dateRaw = getField(row, 'available_date', 'availabledate', 'date', 'available');
    const imageRaw = getField(row, 'image_url', 'imageurl', 'image', 'photo', 'picture', 'img');
    const price = parseFloat(String(priceRaw).replace(/[^0-9.]/g, ''));

    if (!title) { errors.push({ row: row._row, msg: 'Missing title' }); return; }
    if (isNaN(price) || price < 0) { errors.push({ row: row._row, msg: `"${title}" has invalid price: "${priceRaw}"` }); return; }

    let available_date = null;
    if (dateRaw && dateRaw.trim()) {
      const d = new Date(dateRaw);
      if (!isNaN(d.getTime())) available_date = d.toISOString().split('T')[0];
    }

    const image_url = (imageRaw && String(imageRaw).trim().startsWith('http')) ? String(imageRaw).trim() : null;

    valid.push({ title: title.trim(), price, available_date, image_url });
  });
  return { valid, errors };
}

export default function BulkUpload() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef();

  const [rows, setRows] = useState([]);      // parsed preview rows
  const [errors, setErrors] = useState([]);  // validation errors
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [done, setDone] = useState(0);
  const [step, setStep] = useState('idle'); // idle | preview | uploading | complete

  if (!user) { navigate('/login'); return null; }

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setRows([]); setErrors([]); setDone(0); setUploadErrors([]);

    const ext = file.name.split('.').pop().toLowerCase();

    try {
      if (ext === 'csv') {
        const text = await file.text();
        const parsed = parseCSV(text);
        const { valid, errors: errs } = validateRows(parsed);
        setRows(valid);
        setErrors(errs);
        setStep('preview');
      } else if (ext === 'xlsx' || ext === 'xls') {
        // Dynamically import SheetJS
        const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
        // Normalize keys
        const normalized = json.map((row, i) => {
          const out = { _row: i + 2 };
          Object.keys(row).forEach(k => {
            out[k.trim().toLowerCase().replace(/[^a-z_]/g, '')] = row[k];
          });
          return out;
        });
        const { valid, errors: errs } = validateRows(normalized);
        setRows(valid);
        setErrors(errs);
        setStep('preview');
      } else {
        setErrors([{ row: '—', msg: 'Please upload a .csv, .xlsx, or .xls file.' }]);
        setStep('preview');
      }
    } catch (err) {
      setErrors([{ row: '—', msg: 'Could not read file: ' + err.message }]);
      setStep('preview');
    }
  };

  const handleImport = async () => {
    if (!rows.length) return;
    setUploading(true);
    setStep('uploading');
    setUploadErrors([]);
    let successCount = 0;
    const errs = [];

    for (let i = 0; i < rows.length; i++) {
      try {
        await createListing({ ...rows[i], user_id: user.id, status: 'available' });
        successCount++;
        setDone(successCount);
      } catch (e) {
        errs.push(`"${rows[i].title}": ${e.message}`);
      }
    }

    setUploadErrors(errs);
    setUploading(false);
    setStep('complete');
  };

  const reset = () => {
    setRows([]); setErrors([]); setFileName('');
    setDone(0); setUploadErrors([]);
    setStep('idle');
    if (fileRef.current) fileRef.current.value = '';
  };

  const downloadTemplate = () => {
    const csv = `title,price,available_date,image_url\nVintage Lamp,45.00,2025-06-01,https://i.imgur.com/example1.jpg\nWooden Chair,120.00,,https://i.imgur.com/example2.jpg\nBlue Vase,25.50,2025-07-15,`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'marketplace-template.csv';
    a.click();
  };

  return (
    <div className="page">
      <button className="btn btn-g btn-sm" onClick={() => navigate('/dashboard')} style={{ marginBottom: '1.5rem' }}>
        ← Back to Dashboard
      </button>

      <div className="page-header">
        <h1 className="page-title">Bulk Upload Listings</h1>
        <p className="page-sub">Import multiple listings at once from a spreadsheet.</p>
      </div>

      <div className='bulk-grid' style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>

        {/* LEFT — upload area */}
        <div>
          <div className="form-card" style={{ maxWidth: '100%' }}>

            {/* Template download */}
            <div style={{ background: 'var(--cream)', borderRadius: 'var(--r)', padding: '1rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '.9rem' }}>Need a template?</div>
                <div style={{ fontSize: '.78rem', color: 'var(--brown)', marginTop: '.15rem' }}>Download our CSV template and fill it in.</div>
              </div>
              <button className="btn btn-g btn-sm" onClick={downloadTemplate}>⬇ Template</button>
            </div>

            {/* Accepted columns info */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--ink-light)', marginBottom: '.5rem' }}>
                Required Columns
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                {[
                  { col: 'title', note: 'Item name — required' },
                  { col: 'price', note: 'Price in USD — required' },
                  { col: 'available_date', note: 'Date available (optional)' },
                  { col: 'image_url', note: 'Public image link e.g. Imgur (optional)' },
                ].map(({ col, note }) => (
                  <div key={col} style={{ display: 'flex', gap: '.75rem', fontSize: '.85rem', alignItems: 'center' }}>
                    <code style={{ background: '#F0EBE3', padding: '.15rem .5rem', borderRadius: 3, fontFamily: 'monospace', fontSize: '.82rem', color: 'var(--terracotta)', flexShrink: 0 }}>{col}</code>
                    <span style={{ color: 'var(--brown)' }}>{note}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '.75rem', color: 'var(--brown)', marginTop: '.75rem' }}>
                Column names are flexible — "Item Name", "Product", "Cost" etc. are all recognised.
              </div>
            </div>

            {/* Drop zone */}
            <div
              className={`upload-area ${fileName ? 'has' : ''}`}
              onClick={() => fileRef.current?.click()}
            >
              {fileName ? (
                <>
                  <div style={{ fontSize: '1.8rem', marginBottom: '.4rem' }}>📄</div>
                  <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{fileName}</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--brown)', marginTop: '.25rem' }}>Click to choose a different file</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '2.2rem', marginBottom: '.5rem' }}>📊</div>
                  <div style={{ fontWeight: 600, fontSize: '.9rem' }}>Click to upload your spreadsheet</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--brown)', marginTop: '.25rem' }}>.csv, .xlsx, or .xls</div>
                </>
              )}
            </div>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleFile} />
          </div>
        </div>

        {/* RIGHT — preview / results */}
        <div>
          {step === 'idle' && (
            <div className="empty" style={{ padding: '3rem 1rem' }}>
              <div className="empty-icon">📋</div>
              <h3>No file selected</h3>
              <p>Upload a spreadsheet to preview your listings before importing.</p>
            </div>
          )}

          {(step === 'preview' || step === 'uploading' || step === 'complete') && (
            <div>
              {/* Validation errors */}
              {errors.length > 0 && (
                <div className="alert alert-e" style={{ marginBottom: '1rem' }}>
                  <strong>{errors.length} row{errors.length > 1 ? 's' : ''} skipped:</strong>
                  <ul style={{ marginTop: '.4rem', paddingLeft: '1.2rem' }}>
                    {errors.map((e, i) => <li key={i} style={{ fontSize: '.82rem' }}>Row {e.row}: {e.msg}</li>)}
                  </ul>
                </div>
              )}

              {/* Upload errors */}
              {uploadErrors.length > 0 && (
                <div className="alert alert-e" style={{ marginBottom: '1rem' }}>
                  <strong>Some listings failed to save:</strong>
                  <ul style={{ marginTop: '.4rem', paddingLeft: '1.2rem' }}>
                    {uploadErrors.map((e, i) => <li key={i} style={{ fontSize: '.82rem' }}>{e}</li>)}
                  </ul>
                </div>
              )}

              {/* Success */}
              {step === 'complete' && (
                <div className="alert alert-s" style={{ marginBottom: '1rem' }}>
                  ✅ {done} listing{done !== 1 ? 's' : ''} imported successfully!
                </div>
              )}

              {/* Preview table */}
              {rows.length > 0 && step !== 'complete' && (
                <>
                  <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--brown)', marginBottom: '.6rem', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                    Preview — {rows.length} listing{rows.length !== 1 ? 's' : ''} ready to import
                  </div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', overflow: 'hidden', marginBottom: '1rem', maxHeight: 340, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--cream)', position: 'sticky', top: 0 }}>
                          {['Photo', 'Title', 'Price', 'Available Date'].map(h => (
                            <th key={h} style={{ padding: '.6rem .85rem', textAlign: 'left', fontWeight: 600, color: 'var(--ink-light)', fontSize: '.78rem', letterSpacing: '.3px', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'white' : 'var(--warm-white)' }}>
                            <td style={{ padding: '.55rem .85rem' }}>
                              {r.image_url
                                ? <img src={r.image_url} alt='' style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }} />
                                : <span style={{ fontSize: '1.2rem' }}>🛍️</span>}
                            </td>
                            <td style={{ padding: '.55rem .85rem', fontWeight: 500 }}>{r.title}</td>
                            <td style={{ padding: '.55rem .85rem', color: 'var(--terracotta)', fontWeight: 600 }}>${Number(r.price).toFixed(2)}</td>
                            <td style={{ padding: '.55rem .85rem', color: 'var(--brown)' }}>{r.available_date || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: 'flex', gap: '.75rem' }}>
                    <button className="btn btn-g" onClick={reset}>Cancel</button>
                    <button
                      className="btn btn-p"
                      onClick={handleImport}
                      disabled={uploading}
                      style={{ flex: 1 }}
                    >
                      {uploading ? `Importing… (${done}/${rows.length})` : `Import ${rows.length} Listing${rows.length !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </>
              )}

              {/* Complete state actions */}
              {step === 'complete' && (
                <div style={{ display: 'flex', gap: '.75rem' }}>
                  <button className="btn btn-g" onClick={reset}>Upload Another File</button>
                  <button className="btn btn-p" onClick={() => navigate('/dashboard')}>View My Listings</button>
                </div>
              )}

              {rows.length === 0 && errors.length === 0 && step === 'preview' && (
                <div className="alert alert-e">No valid rows found in the file. Please check the format and try again.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
